import React from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { RedirectFullPageBox } from '../components/redirect/redirect-full-page-box';
import { useAuthState } from '../state/auth-state';
import { DEFAULT_TARGET, isAbsoluteUrl, sanitizeTarget } from './redirect-target';

export const RedirectRoute = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { status, intendedRoute, setIntendedRoute, login } = useAuthState();
  const hasRedirectedRef = React.useRef(false);
  const hasAppliedTokenRef = React.useRef(false);

  const tokenFromUrl = searchParams.get('token');

  React.useEffect(() => {
    // SSO redirect: when landing with ?token=..., store the token and then proceed.
    // This must run before the auth check so the user is authenticated before we decide where to send them.
    if (tokenFromUrl && !hasAppliedTokenRef.current) {
      hasAppliedTokenRef.current = true;
      void login(tokenFromUrl).finally(() => {
        setSearchParams({}, { replace: true });
      });
      return;
    }

    // Prevent duplicate navigations (React StrictMode mount/unmount or dependency re-runs).
    if (hasRedirectedRef.current) {
      return;
    }

    // Wait until authentication status has resolved before choosing a destination.
    // If we just applied a token from the URL, stay and wait for status to become loading then authenticated (or error).
    if (status === 'idle' || status === 'loading') {
      return;
    }
    if (hasAppliedTokenRef.current && status === 'unauthenticated') {
      return;
    }

    // Prefer the explicit intended route, then any router state, falling back to the default.
    const fromState = (location.state as { from?: { pathname: string } } | undefined)?.from
      ?.pathname;
    const target = sanitizeTarget(intendedRoute ?? fromState ?? DEFAULT_TARGET);

    // Any unauthenticated state should be sent back to the sign-in route.
    if (status !== 'authenticated') {
      hasRedirectedRef.current = true;
      void navigate('/signin', { replace: true });
      return;
    }

    hasRedirectedRef.current = true;
    // Clear the consumed intended route so future navigations start fresh.
    setIntendedRoute(undefined);

    // Absolute URLs must use a full document navigation.
    if (isAbsoluteUrl(target)) {
      window.location.assign(target);
      return;
    }

    // Internal paths can leverage React Router navigation for history integration.
    if (target.startsWith('/')) {
      void navigate(target, { replace: true });
      return;
    }

    // Fallback: treat unmatched strings as legacy hash (AngularJS) routes.
    window.location.assign(target.startsWith('#') ? target : `#${target}`);
  }, [
    status,
    intendedRoute,
    setIntendedRoute,
    location,
    navigate,
    login,
    tokenFromUrl,
    setSearchParams,
  ]);

  return <RedirectFullPageBox />;
};
